import { Loader2, XCircle } from 'lucide-react';
import React, { useCallback, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { useDeleteV4ManufacturerByRouteManufacturerIdPurchaseOrderAndPurchaseOrderNumberMutation } from 'randmar-api-client';
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, useToast } from 'randmarcomps';
import withManufacturer from "../../../contexts/WithManufacturer";
import Path from '../../../routes/Path';

interface IProps {
  purchaseOrderNumber: string;
  applicationId: string;
  fab?: boolean;
  withReload?: boolean;
  children?: React.ReactNode;
}

const CancelPurchaseOrder: React.FC<IProps> = ({ purchaseOrderNumber, applicationId, fab, withReload, children }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const [cancelPurchaseOrder, { isLoading }] = useDeleteV4ManufacturerByRouteManufacturerIdPurchaseOrderAndPurchaseOrderNumberMutation();

  const handleCancel = useCallback(() => {
    setOpen(false);
  }, []);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleConfirm = useCallback(async () => {
    try {
      await cancelPurchaseOrder({ routeManufacturerId: applicationId, purchaseOrderNumber }).unwrap();
      toast({ variant: "success", title: "Success", description: "Purchase Order cancelled successfully." });
      setOpen(false);
      if (!withReload) {
        navigate(Path.purchaseOrders(applicationId));
      }
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: "Failed to cancel Purchase Order." });
      setOpen(false);
    }
  }, [applicationId, purchaseOrderNumber, withReload, cancelPurchaseOrder, toast, navigate]);

  const fabButton = () => (
    <Button
      variant="ghost"
      size="icon"
      aria-label="remove"
      title={"Cancel purchase order"}
      onClick={handleOpen}
    >
      <XCircle className="h-5 w-5" />
    </Button>
  );

  const normalButton = () => (
    <div
      title={"Cancel purchase order"}
      onClick={handleOpen}>
      {children}
    </div>
  );

  return (
    <>
      {fab ? fabButton() : normalButton()}

      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !isLoading && handleCancel()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Purchase Order {purchaseOrderNumber}</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this purchase order? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirm} disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default withManufacturer(CancelPurchaseOrder);
